import type { DiceLuckPlayerSummary, DiceLuckVerdict, GameStatus } from '@warcarp/api-types'
import { diceLuckPercentile, diceLuckVerdict } from '@warcarp/api-types'

/** Display scale for standardized luck, not a percentage or a capped rating. */
export function diceLuckScore(zScore: number | null, rollCount: number): number | null {
  if (rollCount < 1 || zScore === null || !Number.isFinite(zScore)) return null
  return 100 + 20 * zScore
}

export function formatDiceLuckScore(zScore: number | null, rollCount: number): string {
  const score = diceLuckScore(zScore, rollCount)
  return score === null ? '—' : String(Math.round(score))
}

export function formatDiceLuckSwing(value: number): string {
  const rounded = Math.abs(value) < 0.05 ? 0 : value
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}`
}

/** Product-facing labels for the five statistical bands. */
export function formatDiceLuckVerdict(verdict: DiceLuckVerdict | null): string {
  if (!verdict) return 'Calibrating'
  if (verdict === 'brutal') return 'Very unlucky'
  if (verdict === 'blessed') return 'Very lucky'
  return verdict[0]!.toUpperCase() + verdict.slice(1)
}

/** Shared semantic color treatment for Dice Luck verdict labels. */
export function diceLuckVerdictToneClass(verdict: DiceLuckVerdict | null): string {
  if (verdict === 'lucky' || verdict === 'blessed') return 'text-olive-up'
  if (verdict === 'unlucky' || verdict === 'brutal') return 'text-blood'
  return 'text-ink-soft'
}

/** Requalify saved summaries without rewriting durable snapshots made under an older floor. */
export function withCurrentDiceLuckQualification<T extends DiceLuckPlayerSummary>(row: T): T {
  return {
    ...row,
    verdict: row.zScore === null ? null : diceLuckVerdict(row.zScore, row.rollCount),
    percentile: row.zScore === null ? null : diceLuckPercentile(row.zScore, row.rollCount),
  }
}

/** Compact player-drawer copy; ranking remains inside the full Dice Luck view. */
export function formatDiceLuckRosterLabel(
  row: DiceLuckPlayerSummary | null | undefined,
  loading: boolean,
): string {
  if (!row) return loading ? 'Loading…' : 'Unavailable'
  if (row.rollCount === 0) return 'No rolls'
  return `${formatDiceLuckVerdict(withCurrentDiceLuckQualification(row).verdict)} (${formatDiceLuckScore(row.zScore, row.rollCount)})`
}

/** Shared leaderboard order used by the modal and the in-game player drawer. */
export function rankDiceLuckPlayers(
  rows: readonly DiceLuckPlayerSummary[],
): DiceLuckPlayerSummary[] {
  return [...rows].sort((a, b) => {
    if (a.zScore === null && b.zScore !== null) return 1
    if (a.zScore !== null && b.zScore === null) return -1
    if (a.zScore !== null && b.zScore !== null && b.zScore !== a.zScore) {
      return b.zScore - a.zScore
    }
    return b.netSwing - a.netSwing || a.playerId.localeCompare(b.playerId)
  })
}

/**
 * Legacy completed games can outlive both their detailed event log and the
 * durable dice-luck snapshot introduced later. In that state there is nothing
 * honest to show, so entry points should disappear instead of opening an
 * unavailable modal.
 */
export function shouldShowDiceLuckEntry({
  gameStatus,
  wireEventCount,
  hasSnapshot,
}: {
  gameStatus: GameStatus | undefined
  wireEventCount: number
  hasSnapshot: boolean
}): boolean {
  if (gameStatus === 'ACTIVE') return true
  if (gameStatus !== 'COMPLETE') return false
  return hasSnapshot || wireEventCount > 0
}

export interface DiceLuckDataStateInput {
  visible: boolean
  gameStatus: GameStatus | undefined
  wireEventCount: number
  cachedEventCount: number
  loadedEventCount: number
  queryLoading: boolean
  queryRefetching: boolean
  queryError: boolean
  hasSnapshot: boolean
  hasRetainedResult: boolean
}

export interface DiceLuckDataState {
  hasCompleteEventLog: boolean
  shouldComputeFromEvents: boolean
  loading: boolean
  refreshing: boolean
  unavailable: boolean
}

export interface DiceLuckLiveReadinessInput {
  modalOpen: boolean
  activeGame: boolean
  socketConnected: boolean
  eventLogSynchronized: boolean
  error: boolean
}

/**
 * Once a connected modal catches up, keep its Live label stable while small
 * event deltas load. A real disconnect/error/close still revokes Live at once.
 */
export function resolveDiceLuckLiveReady(
  wasReady: boolean,
  input: DiceLuckLiveReadinessInput,
): boolean {
  if (!input.modalOpen || !input.activeGame || !input.socketConnected || input.error) return false
  return wasReady || input.eventLogSynchronized
}

/**
 * Decide whether the modal may trust the fetched log. The normal game payload
 * carries only a tail, so computing before the `/events` count matches would
 * produce a plausible but wrong whole-game number.
 */
export function resolveDiceLuckDataState(input: DiceLuckDataStateInput): DiceLuckDataState {
  const hasCompleteEventLog = input.cachedEventCount === input.wireEventCount &&
    input.loadedEventCount === input.cachedEventCount
  const detailedLogExpired = input.gameStatus === 'COMPLETE' && input.wireEventCount === 0
  const shouldComputeFromEvents = input.visible && hasCompleteEventLog && !detailedLogExpired
  // A completed game's authoritative eventCount can outlive only part of its
  // physical log (legacy retention/manual cleanup). Once the events query has
  // settled, that mismatch is unrecoverable—not a reason to spin forever.
  const awaitingCompleteLog = !hasCompleteEventLog && (
    input.gameStatus !== 'COMPLETE' || input.queryLoading || input.queryRefetching
  )
  const sourcePending = input.queryLoading || input.queryRefetching || awaitingCompleteLog
  const hasDisplayData = input.hasSnapshot || input.hasRetainedResult
  const loading = input.visible && !hasDisplayData && !input.queryError && (
    sourcePending
  )
  const refreshing = input.visible && hasDisplayData && !input.queryError && sourcePending
  const unavailable = input.visible && input.gameStatus === 'COMPLETE' &&
    !hasDisplayData && !shouldComputeFromEvents && !loading && !input.queryError

  return { hasCompleteEventLog, shouldComputeFromEvents, loading, refreshing, unavailable }
}
